// A20 W7a — the triage queue.
//
// Every bug report from every surface lands in one table (migration 33), so
// this is the one page that answers "what has been reported and did it get
// anywhere". Two columns carry the weight: the mask state (is the attached
// replay redacted or real customer text?) and the GitHub sync state (did this
// ticket actually reach a tracker, or is it sitting here rotting?).
//
// Reads go through the licence backend — never the table, never storage. See
// ./api.ts for why, and for what happens when the read is not deployed yet.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { RefreshCw, Film, Server, Building2 } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { Kpi } from '@/components/shared/Kpi';
import { Pill } from '@/components/shared/badges';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { GithubSyncChip, MaskPill, StatusPill } from './chips';
import {
  listTickets, severityTint, sourceLabel, surfaceLabel, formatDuration,
  type SupportTicketRow, type TicketStatus, type SurfaceKey,
} from './api';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface-raised)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12.5, verticalAlign: 'top' };

const STATUS_TABS: Array<{ key: TicketStatus | ''; label: string }> = [
  { key: '', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'triaged', label: 'Triaged' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const SURFACE_OPTIONS: Array<{ key: SurfaceKey | ''; label: string }> = [
  { key: '', label: 'All surfaces' },
  { key: 'tprm7.admin', label: 'TPRM7 Admin' },
  { key: 'tprm7.portal', label: 'TPRM7 Portal' },
  { key: 'tprm7.trust', label: 'Trust Center' },
  { key: 'license.console', label: 'Licence Console' },
  { key: 'license.portal', label: 'Customer Portal' },
];

export function SupportTicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicketRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<TicketStatus | ''>('');
  const [surface, setSurface] = useState<SurfaceKey | ''>('');

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      // Filtering client-side as well as passing the params: if the read
      // ignores a query param the tabs still do what they say.
      const rows = await listTickets({ limit: 200 });
      setTickets(rows);
      setState('ready');
    } catch (err) {
      setTickets([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => tickets.filter((t) => {
    if (statusTab && t.status !== statusTab) return false;
    if (surface && t.surface_key !== surface) return false;
    return true;
  }), [tickets, statusTab, surface]);

  const kpis = useMemo(() => {
    const open = tickets.filter((t) => t.status === 'open' || t.status === 'triaged' || t.status === 'in_progress').length;
    const blockers = tickets.filter((t) => t.severity === 'blocker' && t.status !== 'resolved' && t.status !== 'closed').length;
    const withReplay = tickets.filter((t) => t.capture_available).length;
    const unsynced = tickets.filter((t) => !t.github_issue_url && t.github_sync_state !== 'skipped').length;
    return { open, blockers, withReplay, unsynced };
  }, [tickets]);

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>{[0, 1, 2, 3, 4].map((i) => <TableRowSkeleton key={i} cols={[90, 200, 110, 70, 80, 110, 80]} />)}</tbody>
        </table>
      </div>
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Support triage</h1>
          <p className="page-subtitle">
            Every report filed by the Polestead Reporter — three TPRM7 apps, both licence consoles, and on-prem
            installs draining through phone-home. Open one to replay the session that produced it.
          </p>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={tickets.length === 0}
        emptyIcon="inbox"
        emptyTitle="No reports yet"
        emptyBody="Nothing has been filed. Reports arrive from the report button in every product surface, and from on-prem installs on their next phone-home."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          <Kpi label="Open" value={kpis.open} icon="inbox" />
          <Kpi label="Blockers" value={kpis.blockers} icon="alert-triangle" accent={kpis.blockers > 0 ? '#DC2626' : undefined} hint={kpis.blockers > 0 ? 'someone cannot work' : 'none outstanding'} />
          <Kpi label="With a replay" value={kpis.withReplay} icon="film" hint={`of ${tickets.length} reports`} />
          <Kpi label="Not in GitHub" value={kpis.unsynced} icon="git-branch" accent={kpis.unsynced > 0 ? '#C2410C' : '#16A34A'} hint={kpis.unsynced > 0 ? 'pending or failed sync' : 'all filed or skipped'} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key || 'all'}
              onClick={() => setStatusTab(t.key)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: `1px solid ${statusTab === t.key ? 'var(--accent)' : 'var(--border)'}`,
                background: statusTab === t.key ? 'var(--accent-light)' : 'var(--surface)', color: statusTab === t.key ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 12.5, fontWeight: statusTab === t.key ? 600 : 400, cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
          <select
            value={surface}
            onChange={(e) => setSurface(e.target.value as SurfaceKey | '')}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12.5, background: 'var(--surface)', marginLeft: 'auto' }}
          >
            {SURFACE_OPTIONS.map((s) => <option key={s.key || 'all'} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <th style={th}>Ticket</th>
                  <th style={th}>Report</th>
                  <th style={th}>Where</th>
                  <th style={th}>Impact</th>
                  <th style={th}>Replay</th>
                  <th style={th}>GitHub</th>
                  <th style={th}>Filed</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                      No reports match this filter.
                    </td>
                  </tr>
                ) : filtered.map((t) => {
                  const sev = severityTint(t.severity);
                  return (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`/support/tickets/${t.id}`)}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    >
                      <td style={td}>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                          {t.ticket_ref}
                        </span>
                        <div style={{ marginTop: 4 }}><StatusPill status={t.status} /></div>
                      </td>
                      <td style={{ ...td, maxWidth: 340 }}>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{t.title}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 11.5, marginTop: 2 }}>
                          {sourceLabel(t.source)}
                          {t.reporter_role ? ` · ${t.reporter_role}` : ''}
                        </div>
                      </td>
                      <td style={td}>
                        <div style={{ color: 'var(--text-primary)' }}>{surfaceLabel(t.surface_key)}</div>
                        {t.route && (
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t.route}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                          {t.instance_id && (
                            <span title={`On-prem install ${t.instance_id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                              <Server size={10} /> on-prem
                            </span>
                          )}
                          {t.external_org_id && (
                            <span title={`Org ${t.external_org_id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                              <Building2 size={10} /> org
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={td}><Pill bg={sev.bg} fg={sev.fg}>{t.severity}</Pill></td>
                      <td style={td}>
                        {t.capture_available ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-primary)' }}>
                              <Film size={11} /> {formatDuration(t.capture_duration_ms)}
                            </span>
                            {/* The claim AND the server's verdict. A row whose
                                capture contradicts its own "redacted" label has
                                to look different from the 200 that do not. */}
                            <MaskPill masked={t.masked} verdict={t.masking_verdict} hasCapture />
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>None</span>
                        )}
                      </td>
                      <td style={td} onClick={(e) => e.stopPropagation()}>
                        <GithubSyncChip
                          state={t.github_sync_state}
                          url={t.github_issue_url}
                          repo={t.github_repo}
                          issueNumber={t.github_issue_number}
                          error={t.github_sync_error}
                          createdAt={t.created_at}
                        />
                      </td>
                      <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(t.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </StateFrame>
    </div>
  );
}

export default SupportTicketsPage;
